/**
 * The handoff between an in-page agent and the booking form.
 *
 * The form lives inside a modal that is unmounted until someone opens it, so a
 * tool cannot hand it a draft directly. The tool stages the draft here and the
 * form drains it whenever it next mounts.
 */
import { trialBookingDraftSchema, type TrialBookingDraft } from '@/lib/capabilities/schemas';

const KEY = 'me.agent.staged_draft';

/**
 * Long enough that a visitor who closes the modal and reopens it still finds
 * their details, short enough that a draft cannot resurface in a later visit
 * and be mistaken for something the visitor typed.
 */
const TTL_MS = 30 * 60 * 1000;

export interface StagedDraft {
  draft: TrialBookingDraft;
  stagedAt: string;
}

/** sessionStorage throws in some privacy modes, and a booking is worth more than a prefill. */
function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

export function stageDraft(draft: TrialBookingDraft): void {
  const store = storage();
  if (!store) return;

  try {
    const staged: StagedDraft = { draft, stagedAt: new Date().toISOString() };
    store.setItem(KEY, JSON.stringify(staged));
  } catch {
    // A full or unavailable store means no prefill, which is a worse form
    // experience and not a broken one.
  }
}

/** Reading does not consume: reopening the modal should find the draft again. */
export function readStagedDraft(): StagedDraft | null {
  const store = storage();
  if (!store) return null;

  let raw: string | null;
  try {
    raw = store.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as { draft?: unknown; stagedAt?: unknown };
    if (typeof parsed.stagedAt !== 'string') return null;

    const age = Date.now() - new Date(parsed.stagedAt).getTime();
    if (!Number.isFinite(age) || age < 0 || age > TTL_MS) {
      clearStagedDraft();
      return null;
    }

    // Anything in web storage is attacker-reachable, so it is re-parsed rather
    // than trusted to still match the shape it was written in.
    const draft = trialBookingDraftSchema.safeParse(parsed.draft);
    if (!draft.success) {
      clearStagedDraft();
      return null;
    }

    return { draft: draft.data, stagedAt: parsed.stagedAt };
  } catch {
    clearStagedDraft();
    return null;
  }
}

export function clearStagedDraft(): void {
  try {
    storage()?.removeItem(KEY);
  } catch {
    // Nothing to do: the TTL will retire it.
  }
}
